import type { ConditionDto, ObservationDto } from "@repo/data-store";
import type { ConditionId } from "@repo/domain";
import { Toggle } from "@repo/ui/components/ui/toggle";
import type { SwitchListOption } from "@repo/ui/form-fields";
import { cn } from "@repo/ui/lib/utils";
import { DetailAccordion } from "@/shared/common/detail-accordion";
import { formatDate } from "@/shared/lib/date-formats";

const ACTIVE_CLINICAL_STATUSES = new Set(["active", "recurrence", "relapse"]);

/** Active = clinically active (or status unknown). Resolved/inactive/remission are not. */
export function isActiveDiagnosis(c: ConditionDto): boolean {
  return !c.clinicalStatus || ACTIVE_CLINICAL_STATUSES.has(c.clinicalStatus);
}

const INACTIVE_STATUS_LABELS: Record<string, string> = {
  inactive: "Inactive",
  remission: "Remission",
  resolved: "Resolved",
};

function conditionLabel(c: ConditionDto): string {
  return c.code.display ?? c.code.text ?? c.code.code ?? c.conditionId;
}

function symptomLabel(s: ObservationDto): string {
  return s.code.display ?? s.code.text ?? s.code.code ?? "Symptom";
}

/** Severity dot color, mirroring the workbench condition-card. Paired with the severity text so it survives grayscale. */
function severityDotColor(s: ObservationDto): string {
  const key = (
    s.valueCodeableConcept?.display ??
    s.valueCodeableConcept?.text ??
    s.valueCodeableConcept?.code ??
    ""
  ).toLowerCase();
  if (key.includes("severe")) return "bg-red-500";
  if (key.includes("moderate")) return "bg-amber-500";
  if (key.includes("mild")) return "bg-green-500";
  return "bg-foreground/40";
}

/** Read-only symptom chip: severity dot + name + severity text. */
function SymptomChip({ symptom }: { symptom: ObservationDto }) {
  const severity = symptom.valueCodeableConcept?.display ?? symptom.valueCodeableConcept?.text;
  return (
    <span className="inline-flex items-center gap-1.5 rounded bg-muted py-0.5 pr-1.5 pl-2 text-xs text-muted-foreground group-aria-pressed:bg-background">
      <span className={cn("size-1.5 shrink-0 rounded-full", severityDotColor(symptom))} />
      <span className="text-foreground/80">{symptomLabel(symptom)}</span>
      {severity && <span>{severity}</span>}
    </span>
  );
}

/**
 * Rows for the toggle list: every active case diagnosis, plus any condition the
 * plan already addresses (kept visible even if since resolved, so it stays
 * removable). Source ordering of `candidates` is preserved.
 */
export function addressableConditions(candidates: ConditionDto[], addressedIds: string[]): ConditionDto[] {
  const addressed = new Set(addressedIds);
  return candidates.filter((c) => isActiveDiagnosis(c) || addressed.has(c.conditionId));
}

/**
 * One condition as a switch-list option: name, code and any inactive status as
 * the label, its recorded symptoms as chips beneath.
 */
export function conditionToggleOption(c: ConditionDto, symptoms?: ObservationDto[]): SwitchListOption {
  const inactiveLabel = c.clinicalStatus ? INACTIVE_STATUS_LABELS[c.clinicalStatus] : undefined;
  return {
    value: c.conditionId,
    label: (
      <>
        <span className="font-medium">{conditionLabel(c)}</span>
        {c.code.code && <span className="ml-2 text-xs text-muted-foreground">{c.code.code}</span>}
        {inactiveLabel && <span className="ml-2 text-xs text-muted-foreground">· {inactiveLabel}</span>}
      </>
    ),
    description:
      symptoms && symptoms.length > 0 ? (
        <span className="flex flex-wrap gap-1">
          {symptoms.map((s) => (
            <SymptomChip key={s.observationId} symptom={s} />
          ))}
        </span>
      ) : undefined,
  };
}

export interface ConditionsAddressedListProps {
  /** Rows to show — typically `addressableConditions(caseDiagnoses, addressedIds)`. */
  conditions: ConditionDto[];
  addressedIds: ConditionId[];
  /** Resolves a condition's `evidenceObservationIds` to symptom observations. Omit to hide symptoms. */
  symptomsById?: Map<string, ObservationDto>;
  /** Toggle a condition into (`true`) or out of (`false`) the plan's `addresses[]`. */
  onToggle: (conditionId: ConditionId, addressed: boolean) => void;
}

/**
 * Conditions-addressed checklist: one `ConditionAddressedItem` per condition.
 */
export function ConditionsAddressedList({
  conditions,
  addressedIds,
  symptomsById,
  onToggle,
}: ConditionsAddressedListProps) {
  if (conditions.length === 0) {
    return <DetailAccordion.Empty>No active diagnoses</DetailAccordion.Empty>;
  }

  return (
    <ul className="space-y-1.5">
      {conditions.map((c) => (
        <li key={c.conditionId}>
          <ConditionAddressedItem
            condition={c}
            addressed={addressedIds.includes(c.conditionId)}
            symptoms={
              symptomsById
                ? (c.evidenceObservationIds ?? []).flatMap((id) => {
                    const s = symptomsById.get(id);
                    return s ? [s] : [];
                  })
                : undefined
            }
            onToggle={(next) => onToggle(c.conditionId, next)}
          />
        </li>
      ))}
    </ul>
  );
}

interface ConditionAddressedItemProps {
  condition: ConditionDto;
  addressed: boolean;
  /** Omit to hide the symptoms line entirely. */
  symptoms?: ObservationDto[];
  onToggle: (addressed: boolean) => void;
}

/**
 * One condition as a pressable card: pressed means the plan addresses it. The
 * card itself is the control, so state reads from the whole card (filled with a
 * tinted vs dashed and muted) rather than from a switch off to one side.
 */
export function ConditionAddressedItem({ condition: c, addressed, symptoms, onToggle }: ConditionAddressedItemProps) {
  const inactiveLabel = c.clinicalStatus ? INACTIVE_STATUS_LABELS[c.clinicalStatus] : undefined;
  const onset = c.onsetDateTime ?? c.onsetPeriod?.start;
  const onsetLabel = onset ? formatDate(onset) : c.onsetString;
  return (
    <Toggle
      pressed={addressed}
      onPressedChange={onToggle}
      className={cn(
        "group h-auto w-full items-start justify-start whitespace-normal border px-2.5 py-2 text-left font-normal",
        addressed
          ? "border-primary/30 bg-primary/5 hover:bg-primary/10 aria-pressed:bg-primary/5"
          : "border-dashed text-muted-foreground hover:border-solid hover:bg-muted/50",
      )}
    >
      <span className="min-w-0 flex-1 space-y-1">
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span className={cn("font-medium", addressed && "text-foreground")}>{conditionLabel(c)}</span>
          {c.code.code && <span className="text-xs text-muted-foreground tabular-nums">{c.code.code}</span>}
          {inactiveLabel && <span className="text-xs text-muted-foreground">· {inactiveLabel}</span>}
          {onsetLabel && <span className="text-xs text-muted-foreground">· onset {onsetLabel}</span>}
        </span>
        {symptoms && symptoms.length > 0 && (
          <span className="flex flex-wrap gap-1">
            {symptoms.map((s) => (
              <SymptomChip key={s.observationId} symptom={s} />
            ))}
          </span>
        )}
      </span>
    </Toggle>
  );
}
